import CreditCardOutlined from '@mui/icons-material/CreditCardOutlined';
import { FormControlLabel, Stack, Switch } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { BreadcrumbMeta } from 'v2/components/Breadcrumb/Breadcrumb';
import EmptyStateOverview from 'v2/components/EmptyStateOverview/EmptyStateOverview';
import OverviewContent from 'v2/components/OverviewContent/OverviewContent';
import SearchAndFilters from 'v2/components/SearchAndFilters/SearchAndFilters';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useAccessCards from 'v2/hooks/useAccessCards/useAccessCards';
import AccessCardsTable from './components/AccessCardsTable';
import { useAccessCardsSearchAndFilters } from './useAccessCardsSearchAndFilters';

const AccessCardsOverview: React.FC = () => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const { accessCards, loadingAccessCards, blockAccessCard } = useAccessCards({
    translation,
    handleFetchError: (msg) => showSnackbar(msg, { severity: 'error' })
  });

  const { filterStr, setFilterStr, hideBlocked, setHideBlocked, filteredCards, countCaption, chips, sections } =
    useAccessCardsSearchAndFilters({ accessCards });

  const breadcrumb = {
    title: translation.access_cards,
    crumbs: [{ text: translation.cards, path: '/cards' }]
  };

  return (
    <>
      <BreadcrumbMeta breadcrumb={breadcrumb} />
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        justifyContent="space-between"
        spacing={2}
      >
        <SearchAndFilters
          filterStr={filterStr}
          onFilterStrChange={setFilterStr}
          sections={sections}
          chips={chips}
          countCaption={countCaption}
        />
        <FormControlLabel
          control={
            <Switch
              id="hide-blocked-cards-switch"
              checked={hideBlocked}
              onChange={(_, checked) => setHideBlocked(checked)}
            />
          }
          label={translation.hide_blocked_cards}
          labelPlacement="end"
          sx={{
            whiteSpace: 'nowrap',
            flexShrink: 0,
            mt: '2px',
            alignSelf: { xs: 'flex-end', sm: 'auto' }
          }}
        />
      </Stack>
      <OverviewContent
        loading={loadingAccessCards}
        hasData={accessCards.length > 0}
        hasResults={filteredCards.length > 0}
        table={<AccessCardsTable entries={filteredCards} loading={loadingAccessCards} onBlockCard={blockAccessCard} />}
        noResults={
          <EmptyStateOverview
            icon={CreditCardOutlined}
            title={translation.no_results}
            content={translation.no_results_description}
          />
        }
        noData={
          <EmptyStateOverview
            icon={CreditCardOutlined}
            title={translation.no_access_cards}
            content={translation.no_access_cards_description}
          />
        }
      />
    </>
  );
};

export default AccessCardsOverview;
